import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Input, Label } from '@pantry/ui';
import { useAuth } from '../../contexts/useAuth';
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export const Route = createFileRoute('/_auth/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useLingui();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPasswordForEmail } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await resetPasswordForEmail(email);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Failed to send reset email`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="space-y-8">
        {/* Success State */}
        <div className="space-y-4 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            <Trans>Check your email</Trans>
          </h1>
          <p className="text-muted-foreground">
            <Trans>
              We&apos;ve sent a password reset link to{' '}
              <strong className="text-foreground">{email}</strong>. Click the link in the email to
              reset your password.
            </Trans>
          </p>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
          <div className="flex gap-3">
            <EnvelopeIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="font-medium">
                <Trans>Didn&apos;t receive the email?</Trans>
              </p>
              <p className="mt-1 text-amber-700">
                <Trans>
                  Check your spam folder, or{' '}
                  <button
                    onClick={() => {
                      setIsSuccess(false);
                      setEmail('');
                    }}
                    className="font-medium underline hover:no-underline"
                  >
                    try again with a different email
                  </button>
                  .
                </Trans>
              </p>
            </div>
          </div>
        </div>

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to sign in</Trans>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          <Trans>Forgot password?</Trans>
        </h1>
        <p className="text-muted-foreground">
          <Trans>No worries, we&apos;ll send you reset instructions.</Trans>
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">
            <Trans>Email address</Trans>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder={t`you@example.com`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
            className="h-11"
          />
        </div>

        <Button type="submit" className="w-full h-11 font-medium" disabled={isLoading}>
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <Trans>Sending...</Trans>
            </span>
          ) : (
            <Trans>Send reset link</Trans>
          )}
        </Button>
      </form>

      {/* Back to login link */}
      <Link
        to="/login"
        className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <Trans>Back to sign in</Trans>
      </Link>
    </div>
  );
}
